import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Phone, FileText, CreditCard, Lock, ShoppingCart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { items, totalPrice } = useCart();

  const [formData, setFormData] = useState({
    shipping_address: '',
    shipping_city: '',
    shipping_phone: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(price);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.shipping_address || !formData.shipping_city || !formData.shipping_phone) {
      toast.error("Lütfen tüm zorunlu alanları doldurun");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/checkout/create-session`, {
        city: formData.shipping_city,
        address: formData.shipping_address,
        phone: formData.shipping_phone,
        notes: formData.notes
      });

      // Paratika ödeme sayfasına yönlendir
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error("Ödeme sayfası oluşturulamadı");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ödeme başlatılamadı");
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <ShoppingCart className="h-24 w-24 text-slate-300 mx-auto mb-6" />
        <h2 className="text-2xl font-bold text-slate-700 mb-2">Sepetiniz Boş</h2>
        <p className="text-slate-500 mb-6">Önce sepete ürün ekleyin.</p>
        <Button onClick={() => navigate('/products')} className="bg-orange-500 hover:bg-orange-600">
          Ürünleri Keşfet
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="checkout-page">
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-slate-900 mb-6">Ödeme</h1>

        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left - Shipping Info */}
            <div className="lg:col-span-2 space-y-6">
              {/* Shipping Address */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-orange-500" />
                    Teslimat Adresi
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="shipping_city">Şehir *</Label>
                    <Input
                      id="shipping_city"
                      name="shipping_city"
                      placeholder="İstanbul"
                      value={formData.shipping_city}
                      onChange={handleChange}
                      className="mt-1"
                      data-testid="city-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="shipping_address">Adres *</Label>
                    <Textarea
                      id="shipping_address"
                      name="shipping_address"
                      placeholder="Mahalle, sokak, bina no, daire no..."
                      value={formData.shipping_address}
                      onChange={handleChange}
                      className="mt-1"
                      rows={3}
                      data-testid="address-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="shipping_phone">Telefon *</Label>
                    <Input
                      id="shipping_phone"
                      name="shipping_phone"
                      type="tel"
                      placeholder="05XX XXX XX XX"
                      value={formData.shipping_phone}
                      onChange={handleChange}
                      className="mt-1"
                      data-testid="phone-input"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Order Notes */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-orange-500" />
                    Sipariş Notu (Opsiyonel)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    name="notes"
                    placeholder="Siparişinizle ilgili notlarınız..."
                    value={formData.notes}
                    onChange={handleChange}
                    rows={3}
                    data-testid="notes-input"
                  />
                </CardContent>
              </Card>

              {/* Payment Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-orange-500" />
                    Ödeme
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-slate-50 rounded-lg p-4 flex items-center gap-4">
                    <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center border">
                      <Lock className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium">Güvenli Ödeme</p>
                      <p className="text-sm text-slate-500">
                        Stripe ile 256-bit SSL şifreli güvenli ödeme
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-500 mt-4">
                    "Ödemeyi Tamamla" butonuna tıkladığınızda güvenli ödeme sayfasına yönlendirileceksiniz.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Right - Order Summary */}
            <div className="lg:col-span-1">
              <Card className="sticky top-24">
                <CardHeader>
                  <CardTitle>Sipariş Özeti</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Items */}
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {items.map((item) => (
                      <div key={item.id} className="flex gap-3">
                        <div className="w-16 h-16 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
                          {item.product_image ? (
                            <img
                              src={item.product_image}
                              alt={item.product_name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-400">
                              <ShoppingCart className="h-6 w-6" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium line-clamp-1">{item.product_name}</p>
                          <p className="text-xs text-slate-500">{item.quantity} adet</p>
                          <p className="text-sm font-bold text-orange-600">{formatPrice(item.subtotal)}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <Separator />

                  {/* Totals */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-slate-600">
                      <span>Ara Toplam</span>
                      <span>{formatPrice(totalPrice)}</span>
                    </div>
                    <div className="flex justify-between text-slate-600">
                      <span>Kargo</span>
                      <span className="text-green-600">Ücretsiz</span>
                    </div>
                  </div>

                  <Separator />

                  <div className="flex justify-between text-lg font-bold">
                    <span>Toplam</span>
                    <span className="text-orange-600">{formatPrice(totalPrice)}</span>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-orange-500 hover:bg-orange-600 h-12"
                    disabled={loading}
                    data-testid="complete-payment-btn"
                  >
                    {loading ? 'İşleniyor...' : 'Ödemeyi Tamamla'}
                  </Button>

                  <p className="text-xs text-center text-slate-500">
                    Sipariş vererek{' '}
                    <a href="/terms" className="text-orange-500 hover:underline">Kullanım Koşulları</a>
                    'nı kabul etmiş olursunuz.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CheckoutPage;
